import type { JSX, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

/**
 * Voice-activity state for the voice float (DESIGN.md §4, the voice-presence
 * entry). Speaking is CLIENT-LOCAL state: Discord's SPEAKING_START/STOP events
 * fire on every client for everyone in the voice channel, so it never touches
 * the Colyseus schema. Production wiring: App.tsx subscribes via
 * useDiscordSpeaking (Discord SPEAKING_START/STOP over the SDK's RPC socket)
 * and mounts the provider behind speakingGate. The dev-mock mounts it from
 * the cycler's speaking axis instead.
 */

/** How the float renders.
 *  - 'live': the real spring + envelope (production behavior).
 *  - 'hover' | 'peak': deterministic stills at the hover floor / a held-burst
 *    altitude — dev-mock diagnostics so visual baselines can capture the float
 *    even though the suite runs with reducedMotion: 'reduce' (under which the
 *    live indicator is, by design, absent). Never used in production. */
export type SpeakingPose = 'live' | 'hover' | 'peak';

export interface SpeakingState {
  speakingIds: ReadonlySet<string>;
  pose: SpeakingPose;
}

const SpeakingContext = createContext<SpeakingState | null>(null);

export function SpeakingProvider({
  speakingIds,
  pose = 'live',
  children,
}: {
  speakingIds: ReadonlySet<string>;
  pose?: SpeakingPose;
  children: ReactNode;
}): JSX.Element {
  const value = useMemo<SpeakingState>(() => ({ speakingIds, pose }), [speakingIds, pose]);
  return <SpeakingContext.Provider value={value}>{children}</SpeakingContext.Provider>;
}

/** `null` when no provider is mounted (dev-real, tests, full-screen fallbacks
 *  outside the page shell) — consumers treat that as "feature off". Suppressed
 *  surfaces no longer unmount the provider; they receive an empty set. */
export function useSpeakingState(): SpeakingState | null {
  return useContext(SpeakingContext);
}
